import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";

import { normalizeBackendOpportunity } from "@/lib/opportunity-prefill-normalize";

const API = `${window.location.origin}/api`;

/**
 * Edit-mode: fetcha l'opportunità tracciata e ritorna lo snapshot
 * normalizzato pronto per popolare il form.
 *
 * Estratto da App.js il 25/02/2026 (166 righe del useEffect originale).
 * Refactor il 25/02/2026 sessione 5: la funzione pura
 * `normalizeBackendOpportunity` è stata spostata in
 * `lib/opportunity-prefill-normalize.js` per consentire i unit-test
 * Jest senza dover transformare axios (ESM).
 *
 * @param {object} opts
 * @param {string} opts.editingOpportunityId - URL param `?opportunityId=...`
 * @param {boolean} opts.isReadonly - include_deleted nel fetch
 * @param {boolean} opts.virtualDuplicate - override pipeline a Indiretti
 * @param {string} opts.dupTarget - "clinico" | "orto"
 *
 * @returns {{data, loading, error}}
 */
export function useOpportunityEditPrefill({
  editingOpportunityId,
  isReadonly,
  virtualDuplicate,
  dupTarget,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!editingOpportunityId) {
      setData(null);
      setLoading(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/opportunities/created/${editingOpportunityId}`, {
        params: isReadonly ? { include_deleted: 1 } : {},
      })
      .then(({ data: raw }) => {
        if (cancelled || !raw) return;
        const normalized = normalizeBackendOpportunity(raw, { virtualDuplicate, dupTarget, isReadonly });
        setData(normalized);
        // SELF-HEALING notice (19/06/2026): se il backend ha auto-corretto
        // i prezzi BDD-legacy a quelli dell'assicurazione attiva via
        // read-time audit, mostra un toast informativo all'utente. Il
        // fix è già stato persistito su MongoDB; al prossimo SAVE va
        // anche su GHL automaticamente. Non bloccante, non rumoroso —
        // solo trasparenza.
        if (raw._audit_self_healed && raw._audit_corrections > 0) {
          toast.info(
            `Auto-allineati ${raw._audit_corrections} prezzi al listino assicurazione`,
            { duration: 5000 },
          );
        }
      })
      .catch((err) => {
        console.error("Edit-mode fetch failed:", err);
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 404) {
          // Opp fantasma: eliminata da GHL ma ancora presente nel Pipeline/
          // Archivio locale finché il job di pulizia non la rimuove.
          toast.error(
            detail && /GHL|tracciata/i.test(detail)
              ? "Questa opportunità non esiste più su GoHighLevel (eliminata). Non è modificabile."
              : "Impossibile caricare l'opportunità da modificare",
          );
        } else {
          toast.error("Impossibile caricare l'opportunità da modificare");
        }
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editingOpportunityId, isReadonly, virtualDuplicate, dupTarget]);

  return { data, loading, error };
}

// Re-export per backward compat: alcune parti del codice potrebbero
// importare la funzione pura da qui. Preferire l'import diretto da
// `@/lib/opportunity-prefill-normalize` nei nuovi file.
export { normalizeBackendOpportunity };
